import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section } from "../../components/Common";
import { toast } from "sonner";
import { Send, Eye } from "lucide-react";

export default function AdminNotifications() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", audience: "all", send_sms: false, send_whatsapp: false });

  const load = async () => {
    const { data } = await api.get("/admin/notifications");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/notifications", form);
      const sms = data?.sms?.attempted || 0;
      const wa = data?.whatsapp?.attempted || 0;
      let extra = "";
      if (sms || wa) {
        const mocked = (sms && data.sms.mocked) || (wa && data.whatsapp.mocked);
        extra = mocked
          ? ` · SMS/WhatsApp: ${sms + wa} recipients (Twilio MOCKED — add keys in backend .env)`
          : ` · SMS sent: ${data.sms.sent}/${sms} · WA sent: ${data.whatsapp.sent}/${wa}`;
      }
      toast.success(`Notification sent${extra}`);
      setForm({ title: "", body: "", audience: "all", send_sms: false, send_whatsapp: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Broadcast updates to students, teachers, or parents." />
      <Section title="New broadcast">
        <form onSubmit={submit} className="space-y-4" data-testid="notif-form">
          <div>
            <label className="text-sm font-semibold text-slate-700">Audience</label>
            <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
              data-testid="notif-audience" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none">
              {["all", "students", "teachers", "parents"].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="notif-title" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Message</label>
            <textarea required rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="notif-body" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
          </div>
          <button type="submit" data-testid="notif-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
            <Send className="h-4 w-4" /> Send
          </button>
          <div className="flex flex-wrap gap-4 pt-2 border-t border-slate-100">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.send_sms} onChange={(e) => setForm({ ...form, send_sms: e.target.checked })}
                data-testid="notif-sms" />
              Also send SMS
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.send_whatsapp} onChange={(e) => setForm({ ...form, send_whatsapp: e.target.checked })}
                data-testid="notif-whatsapp" />
              Also send on WhatsApp
            </label>
            <span className="text-xs text-amber-600">Twilio is MOCKED until keys are added.</span>
          </div>
        </form>
      </Section>

      <div className="mt-8">
        <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">Recent</h2>
        <div className="space-y-3">
          {list.map((n) => (
            <div key={n.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`admin-notice-${n.id}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-slate-900">{n.title}</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{n.audience}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{n.body}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <p className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</p>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid={`notice-reads-${n.id}`}>
                  <Eye className="h-3.5 w-3.5 text-blue-500" />
                  {n.reads?.total ? (
                    <>Seen by <b className="text-slate-700">{n.reads.students}</b> student{n.reads.students === 1 ? "" : "s"} · <b className="text-slate-700">{n.reads.parents}</b> parent{n.reads.parents === 1 ? "" : "s"}{n.reads.teachers > 0 && <> · <b className="text-slate-700">{n.reads.teachers}</b> teacher{n.reads.teachers === 1 ? "" : "s"}</>}</>
                  ) : (
                    "Not seen yet"
                  )}
                </span>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-slate-500">No notifications yet.</p>}
        </div>
      </div>
    </div>
  );
}
